import { type } from "arktype";
import { incomeSplitterSchema } from "./incomeSplitter.models.js";
/**
 * Delete-and-replace recognition is ON for this budget iff this object is PRESENT. It carries no
 * settings, and that emptiness is the decision, not an oversight.
 *
 * It used to hold `autoAcceptThresholds: {amtProximity, descriptionSimilarity}` — two scalars copied
 * into every budget at creation, from an era when users were asked to set them. ADR:
 * delete-and-replace-recognition DELETES that field rather than migrating it: the recognizer is now
 * link → identity → recency, tuned by constants in `deleteReplaceIdentity.ts` /
 * `deleteReplaceRecency.ts`, and there is nothing left for a per-budget number to mean. Keeping one
 * would re-expose a judgment call users have no insight into, and two tuning models at once is exactly
 * the confusion T-366 hit (its report cites a 0.37 bar; the shipped constant was 0.55, and 0.37 was one
 * budget's stored override).
 *
 * ⚠ **Don't collapse this to a boolean.** The object's presence is the user-facing "Auto-Handle
 * Replaced TXNs" toggle (mobile Settings → Configurations) AND what arms the 18h DELETE-card grace
 * period, so its absence is meaningful to several readers. An empty object keeps that contract intact
 * and leaves a place for a genuinely per-budget setting to land later; a boolean would be a wire
 * change across mobile, the e2e flows and every reader, for no behaviour.
 *
 * ⚠ **`"+": "reject"` IS LOAD-BEARING, and its job is the CONTRACT, not type safety.** Arktype infers a
 * bare `type({})` as TypeScript's `object` keyword, which is structurally meaningless on the wire, so
 * the contract snapshotter degrades it to `{kind: "unsupported"}` and raises a `bare-object` flag on
 * every endpoint reaching `BudgetConfig` — 57 of them, each losing the drift detection the snapshot
 * exists to provide. With `"+": "reject"` it infers `{}` and serializes as an object with no
 * properties, which is what it actually is. The runtime half is a genuine bonus: an unknown key is
 * REFUSED, matching `isValidDeleteAndReplaceSettings`.
 *
 * ⚠ **DO NOT READ THE TYPE AS A GUARD — `{}` in TypeScript accepts any non-nullish value**, so
 * `{autoAcceptThresholds: …}`, `"hello"` and `5` all typecheck against it. Nothing here stops a future
 * author writing the removed field; what stops it is the runtime validator plus the tests in
 * `budgetEndpoints.test.ts` that pin the one legacy key being STRIPPED and any other unknown property
 * being REFUSED. Making the type carry the rule was tried and rejected:
 * `type({ "autoAcceptThresholds?": "never", … })` does give the compile error, but it puts
 * `autoAcceptThresholds` back into `api.contract.json` (as an optional `undefined`) across 53
 * endpoints — so the snapshot, which is where a client author looks to see the field is gone, would
 * say it is still there.
 */
const deleteAndReplaceSettingsSchema = type({ "+": "reject" });
/**
 * All six positions in the Roo-tab slot grid are user-pinnable at the schema
 * level. Mobile-side, `cardSorter/reservedSlots.ts` overlays fixed content
 * (matches, all-pouches picker) at whichever positions are configured —
 * pouches pinned to those positions are silently displaced. Editing
 * RESERVED_SLOTS is the way to relocate which slot holds matches / show-all.
 */
export const RooSlotKeys = ["slot_a", "slot_b", "slot_c", "slot_d", "slot_e", "slot_f"];
export const slotConfigSchema = type({
    "slot_a?": "string",
    "slot_b?": "string",
    "slot_c?": "string",
    "slot_d?": "string",
    "slot_e?": "string",
    "slot_f?": "string",
});
export const FavSlotKeys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15"];
export const favConfigSchema = type({
    "1?": "string",
    "2?": "string",
    "3?": "string",
    "4?": "string",
    "5?": "string",
    "6?": "string",
    "7?": "string",
    "8?": "string",
    "9?": "string",
    "10?": "string",
    "11?": "string",
    "12?": "string",
    "13?": "string",
    "14?": "string",
    "15?": "string",
});
// Satisfy checks so slot schemas stay in sync with keys (Pouch.id is a string at runtime).
const _typecheck_slotKeysSubsetOfSchema = {};
const _typecheck_favKeysSubsetOfSchema = {};
/**
 * Arbitrary RevenueCat subscriber attributes (custom key-value pairs) to set on the RevenueCat customer.
 * RevenueCat uses these when calculating targeted offerings, so this is how we drive a budget-specific
 * paywall/offering without shipping a new mobile bundle.
 *
 * **Operator-owned, NOT client-writable.** Because these decide which paywall and price a user sees,
 * `budget.updateWholeConfig` deliberately ignores the client's value and carries the stored one forward
 * (see the modifier comment there) — so this is the one field in `BudgetConfig` a user cannot set for
 * themselves. Keep it that way if you add another config write path.
 *
 * Contract for authors:
 * - Values are plain strings. Keys are alphanumeric + underscore (≤40 chars); values ≤500 chars. Mobile
 *   skips anything out of spec rather than letting it wedge the native attribute batch.
 * - **Removing a key from this object is best-effort** — mobile clears it only if the same app session
 *   applied it. Setting the value to `""` is the GUARANTEED removal (RevenueCat's delete), because
 *   attributes persist server-side per user until overwritten. Prefer `""` over deleting the key.
 * - Reserved RevenueCat keys (`$email`, `$displayName`, …) and the app-managed `FreeTrial` key are
 *   rejected by mobile — the billing service owns those.
 * - Applied to the RevenueCat customer keyed by the **logged-in user**, while the config is
 *   **budget-scoped**: every member of a shared budget receives these attributes, and a departing member
 *   keeps them until explicitly cleared. Intended for household-level targeting.
 * - Values are logged to the analytics event lake with the attribute batch (`r-oTMDLIfI`). Don't put PII
 *   here — the reserved-key filter blocks RevenueCat's own PII keys, not a custom `email` key.
 */
export const revenueCatAttributesSchema = type({
    "[string]": "string",
});
/** Tracks plans and preferences for the budget. */
export const budgetConfigSchema = type({
    incomeSplitter: incomeSplitterSchema,
    /**
     * Custom RevenueCat subscriber attributes; see {@link revenueCatAttributesSchema}.
     * TODO: these will eventually be set from the admin SPA — there is no editor for them yet, so today the
     * only way to populate this object is a direct config write.
     */
    "revenueCatAttributes?": revenueCatAttributesSchema,
    "rooSlots?": slotConfigSchema,
    "fav1Slots?": favConfigSchema,
    billTrackingEnabled: "boolean",
    "autoIgnoreNewZeroTxns?": "boolean",
    /** If undefined, no detection occurs. See {@link deleteAndReplaceSettingsSchema} — presence IS the setting. */
    "deleteAndReplaceSettings?": deleteAndReplaceSettingsSchema,
    /** Trumps membership.auto_await_posted */
    "autoAwaitPosted?": "boolean",
});
//# sourceMappingURL=budgetConfig.models.js.map